import { Room, Resident, PaymentPeriod } from '../types';

/**
 * Checks if a resident's nextPaymentDate matches with their latest payment period's nextPaymentDate.
 * Returns true if there's a discrepancy.
 */
export function hasPaymentDiscrepancy(resident: Resident): boolean {
  if (!resident?.name || !resident.paymentPeriods || resident.paymentPeriods.length === 0) {
    return false;
  }

  // Get the latest payment period
  const sortedPeriods = [...resident.paymentPeriods].sort((a, b) => 
    b.periodNumber - a.periodNumber
  );
  
  const latestPeriod = sortedPeriods[0];
  
  // Check if the resident's nextPaymentDate matches the latest payment period's nextPaymentDate
  return resident.nextPaymentDate !== latestPeriod.nextPaymentDate;
}

/**
 * For residents with payment discrepancies, returns the correct nextPaymentDate based on 
 * their latest payment period.
 */
export function getCorrectPaymentDate(resident: Resident): string | null {
  if (!resident?.name || !resident.paymentPeriods || resident.paymentPeriods.length === 0) {
    return null;
  }

  // Get the latest payment period
  const sortedPeriods = [...resident.paymentPeriods].sort((a, b) => 
    b.periodNumber - a.periodNumber
  );
  
  return sortedPeriods[0].nextPaymentDate;
}

/**
 * Check all residents for payment date discrepancies and return those that have incorrect
 * nextPaymentDate values.
 */
export function findResidentsWithDiscrepancies(rooms: Room[]): Resident[] {
  const residentsWithDiscrepancies: Resident[] = [];
  
  rooms.forEach(room => {
    room.residents.forEach(resident => {
      if (hasPaymentDiscrepancy(resident)) {
        residentsWithDiscrepancies.push(resident);
      }
    });
  });
  
  return residentsWithDiscrepancies;
}

/**
 * Check if a resident should be in late payments but isn't.
 * This checks if their actual payment date (from periods) is overdue but
 * their main nextPaymentDate doesn't reflect this.
 */
export function shouldBeInLatePayments(resident: Resident, currentDate: Date): boolean {
  if (!resident?.name || !resident.paymentPeriods || resident.paymentPeriods.length === 0) {
    return false;
  }

  // Get the latest payment period
  const sortedPeriods = [...resident.paymentPeriods].sort((a, b) => 
    b.periodNumber - a.periodNumber
  );
  
  const latestPeriod = sortedPeriods[0];
  
  // Skip jika kategori terakhir adalah DP (DP tidak masuk daftar tertunggak)
  if (latestPeriod.category === 'DP') {
    return false;
  }
  
  // Check if the latest period's nextPaymentDate is past due
  const periodNextPaymentDate = new Date(latestPeriod.nextPaymentDate);
  periodNextPaymentDate.setHours(0, 0, 0, 0);
  
  // Check if the period's nextPaymentDate is past due
  if (periodNextPaymentDate <= currentDate) {
    // Check if the resident's nextPaymentDate doesn't match or is in the future
    const residentNextPaymentDate = new Date(resident.nextPaymentDate);
    residentNextPaymentDate.setHours(0, 0, 0, 0);
    
    return residentNextPaymentDate > currentDate || 
      resident.nextPaymentDate !== latestPeriod.nextPaymentDate;
  }
  
  return false;
}

/**
 * Find all residents that should be in late payments but aren't due to nextPaymentDate
 * discrepancies.
 */
export function findMissingLatePayments(rooms: Room[]): Resident[] {
  const missingLatePayments: Resident[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  rooms.forEach(room => {
    room.residents.forEach(resident => {
      if (shouldBeInLatePayments(resident, today)) {
        missingLatePayments.push(resident);
      }
    });
  });
  
  return missingLatePayments;
}

/**
 * Checks if a resident should be in late payments based on the correct payment date
 * (irrespective of whether their resident.nextPaymentDate is correct or not)
 */
export function isActuallyPastDue(resident: Resident): boolean {
  if (!resident?.name || !resident.paymentPeriods || resident.paymentPeriods.length === 0) {
    return false;
  }
  
  // Get the latest payment period
  const sortedPeriods = [...resident.paymentPeriods].sort((a, b) => 
    b.periodNumber - a.periodNumber
  );
  
  const latestPeriod = sortedPeriods[0];
  
  // Skip jika kategori terakhir adalah DP (DP tidak masuk daftar tertunggak)
  if (latestPeriod.category === 'DP') {
    return false;
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set ke awal hari untuk perbandingan yang konsisten
  
  // Check if the latest period's nextPaymentDate is past due
  const periodNextPaymentDate = new Date(latestPeriod.nextPaymentDate);
  periodNextPaymentDate.setHours(0, 0, 0, 0);
  
  // Pembayaran dianggap tertunggak jika tanggal jatuh tempo sudah lewat (< hari ini)
  return periodNextPaymentDate < today;
}